import {
  addItemToCollection,
  deleteItemFromCollection,
  getItemFromCollection,
  getItemsFromCollection,
  updateItemInCollection,
} from "directus/src/operations";

import { getUserFields } from "components/user/config";
import { applySorting } from "directus/src/utils";
import { formatDateForAPI } from "utils/utils";

export async function createTimesheetEntry({ item }) {
  try {
    const query = {
      fields: [
        "approved_hours",
        "date",
        "description",
        "employee.avatar",
        "employee.first_name",
        "employee.id",
        "employee.last_name",
        "id",
        "job.id",
        "job.name",
        "logged_hours",
        "milestone.id",
        "milestone.name",
        "tag",
        "tag.id",
        "tag.label",
        "tag.color",
        "project.id",
        "project.name",
        "unapproved_hours",
      ],
    };

    const data = await addItemToCollection("timesheet", item, query);

    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}





export async function fetchJMilestonesForJob({ job }) {
  try {
    const query = {
      fields: ["id", "name"],
      filter: {
        status: {
          _eq: "active",
        },
        job: {
          _eq: job,
        },

        _or: [
          {
            coordinator: {
              user: { _eq: "$CURRENT_USER" },
            },
          },
          {
            job: {
              coordinator: {
                user: { _eq: "$CURRENT_USER" },
              },
            },
          },
          {
            job: {
              project: {
                coordinator: {
                  user: { _eq: "$CURRENT_USER" },
                },
              },
            },
          },
          {
            job: {
              employees: {
                _and: [
                  {
                    employee: {
                      user: { _eq: "$CURRENT_USER" },
                    },
                  },
                  {
                    is_logging_restricted: {
                      _neq: true,
                    },
                  },
                ],
              },
            },
          },
        ],
      },
      sort: ["name"],
    };
    const data = await getItemsFromCollection("milestones", query);
    return data;
  } catch (error) {
    console.error("Error fetching jobs for project:", error);
    throw error;
  }
}

export async function fetchDailyTimesheetForLog({ date, sortBy, sortOrder }) {
  try {
    const query = {
      fields: [
        "id",
        "date",
        "logged_hours",
        "approved_hours",
        "unapproved_hours",
        "description",
        "project.id",
        "project.name",
        "job.id",
        "job.name",
        "milestone.id",
        "milestone.name",
        "tag",
        "tag.id",
        "tag.label",
        "tag.color",
      ],
      filter: {
        date: {
          _eq: date,
        },
        logged_hours: {
          _neq: 0,
        },
        employee: {
          user: { _eq: "$CURRENT_USER" },
        },
      },
      sort: ["-date_created"],
    };
    const data = await getItemsFromCollection("timesheet", query);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

// Fetch daily entries from Draft Timesheet collection
export async function fetchDailyTimesheetForDraftLog({ date }) {
  try {
    const query = {
      fields: [
        "id",
        "date",
        "logged_hours",
        "description",
        "department",
        getUserFields("employee"),
        "tag.id",
        "tag.label",
        "tag.color",
      ],
      filter: {
        date: { _eq: date },
        logged_hours: { _neq: 0 },
        employee: { user: { _eq: "$CURRENT_USER" } },
      },
      sort: ["-date_created"],
    };
    const data = await getItemsFromCollection("draft_timesheet", query);
    return data;
  } catch (error) {
    console.error("Error fetching draft timesheet for day:", error);
    throw error;
  }
}

export async function fetchDailyDailyCapacity({ date }) {
  try {
    const query = {
      fields: ["id", "date", "payroll_hours", "employee", "is_logged"],
      filter: {
        date: {
          _eq: date,
        },
        employee: {
          user: { _eq: "$CURRENT_USER" },
        },
      },
    };
    const data = await getItemsFromCollection("daily_work_capacity", query);
    return data;
  } catch (error) {
    console.error("Error fetching daily capacity:", error);
    throw error;
  }
}

export async function fetchDailyTimesheetForReview({ filters }) {
  try {
    const query = {
      fields: [
        "id",
        "date",
        "logged_hours",
        "employee.first_name",
        "employee.last_name",
        "employee.id",
        "employee.avatar",
        "approved_hours",
        "unapproved_hours",
        "description",
        "project.id",
        "project.name",
        "job.id",
        "job.name",
        "milestone.id",
        "milestone.name",
        "tag",
        "tag.id",
        "tag.label",
        "tag.color",
        "date_updated",
        "tag.id",
        "tag.label",
        "tag.color",
      ],
      filter: {
        logged_hours: {
          _neq: 0,
        },
        reviewer: {
          user: {
            _eq: "$CURRENT_USER"
          }
        }
      },
      sort: ["date_updated", "project.name"],
    };

    if (filters && Object.keys(filters).length > 0) {
      // Deep clone the filters to avoid modifying the original
      const processedFilters = JSON.parse(JSON.stringify(filters));

      // Process each filter to remove properties starting with "_"
      Object.keys(processedFilters).forEach((key) => {
        if (
          processedFilters[key] &&
          typeof processedFilters[key] === "object"
        ) {
          // Remove any properties that start with "_"

          if (key.startsWith("_")) {
            delete processedFilters[key];
            return;
          }
          Object.keys(processedFilters[key]).forEach((prop) => {
            if (prop.startsWith("__")) {
              delete processedFilters[key][prop];
            }
          });
        }

        // If the filter is now empty, remove it entirely
        if (
          processedFilters[key] &&
          Object.keys(processedFilters[key]).length === 0
        ) {
          delete processedFilters[key];
        }
      });
      query.filter = { ...query.filter, ...processedFilters };
    }

    const data = await getItemsFromCollection("timesheet", query);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function fetchDailyJobTimesheet({ filters }) {
  try {
    const query = {
      fields: [
        "id",
        "date",
        "logged_hours",
        "employee.first_name",
        "employee.last_name",
        "employee.id",
        "employee.avatar",
        "approved_hours",
        "unapproved_hours",
        "description",
        "project.id",
        "project.name",
        "job.id",
        "job.name",
        "milestone.id",
        "milestone.name",
        "tag",
        "tag.id",
        "tag.label",
        "tag.color",
        "date_updated",
      ],
      filter: {
        logged_hours: {
          _neq: 0,
        },
      },
      sort: ["date_updated", "project.name"],
    };

    if (filters && Object.keys(filters).length > 0) {
      // Deep clone the filters to avoid modifying the original
      const processedFilters = JSON.parse(JSON.stringify(filters));

      // Process each filter to remove properties starting with "_"
      Object.keys(processedFilters).forEach((key) => {
        if (
          processedFilters[key] &&
          typeof processedFilters[key] === "object"
        ) {
          // Remove any properties that start with "_"

          if (key.startsWith("_")) {
            delete processedFilters[key];
            return;
          }
          Object.keys(processedFilters[key]).forEach((prop) => {
            if (prop.startsWith("__")) {
              delete processedFilters[key][prop];
            }
          });
        }

        // If the filter is now empty, remove it entirely
        if (
          processedFilters[key] &&
          Object.keys(processedFilters[key]).length === 0
        ) {
          delete processedFilters[key];
        }
      });
      query.filter = { ...query.filter, ...processedFilters };
    }

    const data = await getItemsFromCollection("timesheet", query);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function getTimesheetUnlocksItem({ date }) {
  try {
    const query = {
      fields: ["id", "status", "date"],
      filter: {
        date: { _eq: date },
        employee: { user: { _eq: "$CURRENT_USER" } },
      },
    };
    const data = await getItemsFromCollection("timesheet_unlocks", query);

    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function deleteTimesheetEntry({ id }) {
  try {
    const query = {
      fields: ["id"],
    };
    const data = await deleteItemFromCollection("timesheet", id, query);

    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function fetchTimesheetEntry({ timesheetId }) {
  try {
    const query = {
      fields: [
        "approved_hours",
        "date",
        "description",
        "employee.avatar",
        "employee.first_name",
        "employee.last_name",
        "id",
        "job.id",
        "job.name",
        "logged_hours",
        "milestone.id",
        "milestone.name",
        "tag",
        "tag.id",
        "tag.label",
        "tag.color",
        "project.id",
        "project.name",
        "unapproved_hours",
        "reason",
      ],
    };
    const res = await getItemFromCollection("timesheet", timesheetId, query);
    return res;
  } catch (error) {
    console.log(error);
    throw error;
  }
}
